// Methods not using "this" are implementing the interface and cannot be static
/* eslint-disable class-methods-use-this */

import { AxiosResponse } from 'axios';
import VersionedResource from '../VersionedResource';
import { NonAccrualSettings } from './loanaccounts/NonAccrualSettings';
import { StatusSettingsResource } from '../accountstatussettings/StatusSettingsResource';
import { StatusSettings } from '../accountstatussettings/StatusSettings';
import { NonAccrualStatusIndicator } from './statusindicators/NonAccrualStatusIndicator';

export class NonAccrualSettingsResource extends VersionedResource implements StatusSettingsResource {
  public async save(fiId: string, settings: NonAccrualSettings): Promise<NonAccrualSettings> {
    const url = `/api/rcx/config/${fiId}/loan-account-settings/non-accrual`;

    const response: AxiosResponse = await this.apiClient.put(url, settings);
    return response.data.data;
  }

  createEmptySettings(): NonAccrualSettings {
    return {
      statusIndicator: NonAccrualStatusIndicator.UNSET,
      statusCodes: [],
      active: true,
    };
  }

  setIndicatorsOnSettings(settings: StatusSettings, values: string[], conditionalOr: boolean | null) {
    const nonAccrualSettings = settings as NonAccrualSettings;

    const conditional: string = conditionalOr ? '_OR_' : '_AND_';

    let statusIndicator: string = '';

    if (values.includes(NonAccrualStatusIndicator.STATUS_CODE)) {
      statusIndicator = NonAccrualStatusIndicator.STATUS_CODE;
    }

    if (values.includes(NonAccrualStatusIndicator.PRODUCT_CODE)) {
      if (statusIndicator.length > 0) {
        statusIndicator += conditional;
      }
      statusIndicator += NonAccrualStatusIndicator.PRODUCT_CODE;
    }

    if (values.includes(NonAccrualStatusIndicator.NON_ACCRUAL_DATE)) {
      if (statusIndicator.length > 0) {
        statusIndicator += conditional;
      }
      statusIndicator += NonAccrualStatusIndicator.NON_ACCRUAL_DATE;
    }

    nonAccrualSettings.statusIndicator = statusIndicator as NonAccrualStatusIndicator;
  }

  getIndicatorsFromSettings(settings: StatusSettings): string[] | null {
    const nonAccrualSettings = settings as NonAccrualSettings;
    let indicators: string[] = [];
    switch (nonAccrualSettings.statusIndicator) {
      case NonAccrualStatusIndicator.UNSET:
        indicators = [];
        break;

      case NonAccrualStatusIndicator.NON_ACCRUAL_DATE:
        indicators = [NonAccrualStatusIndicator.NON_ACCRUAL_DATE];
        break;

      case NonAccrualStatusIndicator.STATUS_CODE:
        indicators = [NonAccrualStatusIndicator.STATUS_CODE];
        break;

      case NonAccrualStatusIndicator.PRODUCT_CODE:
        indicators = [NonAccrualStatusIndicator.PRODUCT_CODE];
        break;

      case NonAccrualStatusIndicator.PRODUCT_CODE_AND_NON_ACCRUAL_DATE:
      case NonAccrualStatusIndicator.PRODUCT_CODE_OR_NON_ACCRUAL_DATE:
        indicators = [NonAccrualStatusIndicator.PRODUCT_CODE, NonAccrualStatusIndicator.NON_ACCRUAL_DATE];
        break;

      case NonAccrualStatusIndicator.STATUS_CODE_AND_NON_ACCRUAL_DATE:
      case NonAccrualStatusIndicator.STATUS_CODE_OR_NON_ACCRUAL_DATE:
        indicators = [NonAccrualStatusIndicator.STATUS_CODE, NonAccrualStatusIndicator.NON_ACCRUAL_DATE];
        break;

      case NonAccrualStatusIndicator.STATUS_CODE_AND_PRODUCT_CODE:
      case NonAccrualStatusIndicator.STATUS_CODE_OR_PRODUCT_CODE:
        indicators = [NonAccrualStatusIndicator.STATUS_CODE, NonAccrualStatusIndicator.PRODUCT_CODE];
        break;

      default:
        indicators = [NonAccrualStatusIndicator.STATUS_CODE, NonAccrualStatusIndicator.PRODUCT_CODE, NonAccrualStatusIndicator.NON_ACCRUAL_DATE];
        break;
    }

    return indicators;
  }

  getConditionalFromSettings(settings: StatusSettings): string | null {
    const nonAccrualSettings = settings as NonAccrualSettings;

    let conditional: string | null = null;

    if (nonAccrualSettings.statusIndicator.includes('_OR_')) {
      conditional = 'OR';
    } else if (nonAccrualSettings.statusIndicator.includes('_AND_')) {
      conditional = 'AND';
    }

    return conditional;
  }
}

export default NonAccrualSettingsResource;
